import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { ReactElement, useState } from "react";
import { useParams } from "react-router-dom";

import { useSharedCollection } from "../../hooks/query/sharable-collection-link/useSharableCollectionLink";
import Navigation from "../../layout/navigation";

export type QueryParams = {
  page: number;
  limit: number;
  offset: number;
  sortBy?: string;
  sortOrder?: "Asc" | "Desc";
  filters?: {
    name?: string;
    assetType?: string;
  };
};

export default function Share(): ReactElement {
  const { id } = useParams();

  const [queryParams, setQueryParams] = useState<QueryParams>({
    page: 1,
    limit: 16,
    offset: 0,
    sortOrder: "Asc",
  });

  const { isValidating, data, loading, error } = useSharedCollection(id ?? "", {
    baseUrl: `${
      process.env.REACT_APP_BACKEND_API_URL
    }/share/collection/public?limit=${queryParams?.limit}&page=${
      queryParams?.page
    }&sortOrder=${encodeURIComponent(
      queryParams?.sortOrder ?? "Asc"
    )}&name=${encodeURIComponent(queryParams?.filters?.name ?? "")}`,
  });

  if (error) {
    enqueueSnackbar(error.message, {
      variant: "error",
    });

    return (
      <div>
        {error.code} | {error.message}
      </div>
    );
  }

  if (isValidating) {
    return <div>is validating</div>;
  }

  return (
    <>
      <Navigation classes="!pl-0">
        <div className="relative w-full">
          <label className="absolute text-[12px] top-[7px] left-[15px] text-slate-500 z-10">
            Search in collection
          </label>
          <TextField
            className="shadow border-0 rounded-[15px] bg-white w-full"
            placeholder="Enter keyword"
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onChange={(e) =>
              setQueryParams((prev) => ({
                ...prev,
                filters: {
                  name: e.target.value,
                },
              }))
            }
            sx={{
              "& .MuiInputBase-input": {
                paddingTop: "24px",
                paddingBottom: "9px",
              },
              ".MuiOutlinedInput-notchedOutline": {
                border: 0,
                borderRadius: "15px",
              },
            }}
            InputProps={{
              endAdornment: (
                <SearchIcon className="text-[22px] text-slate-400 cursor-pointer" />
              ),
            }}
          />
        </div>
        <FormControl className="w-[110px]">
          <InputLabel className="top-[16px]">Show</InputLabel>
          <Select
            sx={{
              "& .MuiOutlinedInput-notchedOutline": { border: 0 },
              "& legend span": { width: 0, padding: 0 },
              "& .MuiSelect-select": {
                paddingTop: "25px",
                paddingBottom: "8px",
              },
            }}
            className="shadow rounded-[15px] bg-white overflow-hidden h-full border-none"
            value={queryParams.limit}
            onChange={(e) =>
              setQueryParams((prev) => ({
                ...prev,
                limit: parseInt(e.target.value as string),
                offset: 0,
                page: 1,
              }))
            }
            label="Show"
          >
            <MenuItem value={16}>16</MenuItem>
            <MenuItem value={32}>32</MenuItem>
            <MenuItem value={64}>64</MenuItem>
            <MenuItem value={128}>128</MenuItem>
          </Select>
        </FormControl>
      </Navigation>

      <div className="shadow rounded-[15px] p-50 bg-white gap-[20px] basis-[100%] flex-wrap flex">
        <div className="flex w-full justify-items-center h-fit justify-between">
          <div>
            <div className="flex">
              <h2 className="text-[24px] text-rose-600">
                {data?.collectionName}
                <span className="text-[16px] text-slate-400">
                  {" "}
                  ({data?.totalCount})
                </span>
              </h2>
            </div>
          </div>
          <div className="block text-right">
            <FormControl style={{ minWidth: 100 }} size="small">
              <InputLabel className="text-[14px]">Sort by</InputLabel>
              <Select
                className="border-0 rounded-[15px] text-[14px] text-left"
                label="Sort by"
                value={queryParams.sortOrder}
                onChange={(e) =>
                  setQueryParams((prev) => ({
                    ...prev,
                    sortOrder: e.target.value as "Asc" | "Desc",
                  }))
                }
              >
                <MenuItem defaultChecked value="Asc">
                  Newest
                </MenuItem>
                <MenuItem value="Desc">Oldest</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
        {loading && (
          <Box
            sx={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <CircularProgress />
          </Box>
        )}
        {!loading &&
          data?.assets.map((asset) => (
            <Card
              key={asset.id}
              id={asset.id || ""}
              style={{ borderColor: "#E8E8E8", boxShadow: "none" }}
              className="shadow-none min-h-[350px] rounded-[15px] flex flex-col border-solid border-1 w-full max-w-[calc(50%_-_15px)] HD:max-w-[calc(25%_-_15px)] HD+:max-w-[calc(20%_-_16px)] QHD:max-w-[calc(10%_-_18px)]"
            >
              <CardHeader
                action={
                  asset?.downloadUrl && (
                    <div className="">
                      <IconButton
                        onClick={() => {
                          if (asset?.downloadUrl) {
                            const link = document.createElement("a");

                            link.href = asset.downloadUrl;
                            link.download = asset.downloadUrl;

                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        }}
                      >
                        <FileDownloadOutlinedIcon />
                      </IconButton>
                    </div>
                  )
                }
              />
              <div className="mx-[20px] h-full flex items-center justify-center cursor-pointer">
                <img
                  className="text-[120px] max-h-[200px] object-contain"
                  src={`${asset.thumbnailUrl}`}
                  alt={asset.name || ""}
                />
              </div>
              <CardContent className="p-[20px] leading-[17px] last:pb-[10px]">
                <Typography className="mb-[10px] text-[14px] break-keep">
                  {asset.name}
                </Typography>
              </CardContent>
            </Card>
          ))}
        <Pagination
          style={{ display: !data?.assets.length ? "none" : "flex" }}
          color="primary"
          page={queryParams.page}
          onChange={(_, page: number) =>
            setQueryParams((prev) => ({
              ...prev,
              limit: queryParams.limit,
              offset: queryParams.limit * (page - 1),
              page: page,
            }))
          }
          className="w-full flex justify-center mt-5"
          count={Math.ceil((data?.totalCount ?? 0) / queryParams.limit)}
          size="large"
        />
      </div>
    </>
  );
}
